// 全站主题样式（统一为首页风格）
// - 不引入额外依赖，直接复用 Tailwind class 字符串
// - 目标：按钮/表面层/输入框/链接的颜色与交互手感一致

export function cx(...parts) {
  return parts.flat(Infinity).filter(Boolean).join(' ');
}

export const theme = {
  // 首页“玻璃卡”氛围的基础表面
  surfaces: {
    glassCard: cx('rounded-md border border-white/10 bg-white/[0.04]', 'backdrop-blur-sm'),
    // 用于列表项/可点击卡片
    glassItem: cx(
      'rounded-md border border-white/10 bg-white/[0.03]',
      'hover:bg-white/[0.05] transition-colors'
    ),
    // 用于较轻的 hover 行（表格/榜单行）
    glassRow: cx('hover:bg-white/[0.04] transition-colors'),
    // 弹窗面板：比 glassCard 更“沉”一点的底色
    modalPanel: cx('rounded-md border border-white/10 bg-[#0b1220]/90', 'backdrop-blur-sm')
  },

  // 首页链接式按钮（“浏览全部/展开全文”）
  links: {
    subtleUnderline: cx('text-xs text-white/80 hover:text-white', 'underline underline-offset-4')
  },

  // 输入控件：暗色+细边框+蓝色 focus
  inputs: {
    base: cx(
      'w-full rounded-md',
      'bg-white/[0.04] border border-white/10',
      'text-white placeholder:text-white/40',
      'focus:outline-none focus:ring-2 focus:ring-blue-400/25 focus:border-blue-400/30'
    )
  },

  // 按钮体系（以首页“开始聆听”同款 outline 为主）
  buttons: {
    base: cx(
      'inline-flex items-center justify-center',
      'font-medium transition',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/25'
    ),
    size: {
      xs: 'px-2 py-1 rounded-lg text-[11px]',
      sm: 'px-2 py-1 rounded-lg text-xs',
      md: 'px-4 py-2 rounded-md text-sm'
    },
    variant: {
      // “开始聆听”同款
      outline: 'bg-blue-500/15 border border-blue-400/30 text-white hover:bg-blue-500/22',
      // 强调按钮（注册/主要 CTA）
      primary: 'bg-amber-500/15 border border-amber-400/35 text-amber-100 hover:bg-amber-500/20',
      // 危险操作（移除/删除）
      danger: 'bg-red-500/10 border border-red-400/30 text-white hover:bg-red-500/16',
      // 轻量文字按钮（不做背景/边框）
      ghost: 'bg-transparent border border-transparent text-white/90 hover:text-white'
    }
  },

  // 徽章/Tag（用于会员类型、状态、标签）
  badges: {
    base: cx(
      'inline-flex items-center gap-1',
      'px-2 py-0.5 rounded-full text-xs',
      'border',
      'select-none'
    ),
    variant: {
      neutral: 'bg-white/[0.03] border-white/10 text-white/75',
      info: 'bg-blue-500/10 border-blue-400/25 text-blue-100',
      success: 'bg-emerald-500/10 border-emerald-400/25 text-emerald-100',
      warning: 'bg-amber-500/10 border-amber-400/25 text-amber-100',
      danger: 'bg-red-500/10 border-red-400/25 text-red-100',
      purple: 'bg-purple-500/10 border-purple-400/25 text-purple-100'
    }
  },

  // 提示条/Alert（错误/成功/警告/信息）
  alerts: {
    base: cx('rounded-lg px-3 py-2 border', 'text-sm'),
    variant: {
      neutral: 'bg-white/[0.03] border-white/10 text-white/80',
      info: 'bg-blue-500/10 border-blue-400/25 text-blue-100',
      success: 'bg-emerald-500/10 border-emerald-400/25 text-emerald-100',
      warning: 'bg-amber-500/10 border-amber-400/25 text-amber-100',
      danger: 'bg-red-500/10 border-red-400/25 text-red-100'
    }
  },

  // 表格（管理页/列表页统一质感）
  tables: {
    // 注意：这里不能引用 theme.*（会触发 no-use-before-define），所以直接展开 glassCard 的类
    wrapper: cx('border border-white/10 bg-white/[0.04] backdrop-blur-sm', 'rounded-lg overflow-hidden'),
    table: 'w-full',
    thead: 'bg-white/[0.03]',
    th: 'px-4 py-2 text-left text-xs font-medium text-white/55 uppercase tracking-wide',
    tbody: 'divide-y divide-white/10',
    tr: cx('transition-colors', 'hover:bg-white/[0.04]'),
    td: 'px-4 py-2 text-sm text-white/75',
    tdMuted: 'px-4 py-2 text-sm text-white/60',
    empty: 'p-4 text-white/60'
  },

  // 空状态（无数据/加载更多提示等）
  emptyState: {
    text: 'text-white/60',
    hint: 'text-white/45'
  },

  // 下拉选择框（自定义 dropdown，渐变蓝底）
  dropdown: {
    // 触发按钮
    trigger: cx(
      'w-full h-[26px] rounded-md border border-white/10 text-white/90 px-2',
      'bg-gradient-to-r from-blue-950 via-blue-800 to-blue-950',
      'focus:ring-2 focus:ring-blue-400/25 focus:border-blue-400/30',
      'disabled:opacity-40 disabled:cursor-not-allowed',
      'flex items-center justify-between gap-1 text-left',
      'transition-colors hover:border-white/20'
    ),
    // 紧凑版触发按钮（用于翻译语言等小下拉）
    triggerCompact: cx(
      'w-full h-[26px] rounded-md border border-white/10 text-white/90 px-1.5 text-[11px]',
      'bg-gradient-to-r from-blue-950 via-blue-800 to-blue-950',
      'focus:ring-2 focus:ring-blue-400/25 focus:border-blue-400/30',
      'flex items-center justify-start truncate',
      'transition-colors hover:border-white/20'
    ),
    // 下拉列表容器
    menu: cx(
      'absolute left-0 min-w-full w-auto z-20 overflow-hidden',
      'rounded-lg border border-white/10',
      'bg-gradient-to-r from-blue-950 via-blue-800 to-blue-950',
      'shadow-[0_10px_25px_rgba(0,0,0,0.35)]'
    ),
    // 向上弹出时追加
    menuUp: 'bottom-full mb-1',
    // 向下弹出时追加
    menuDown: 'top-full mt-1',
    // 下拉选项
    option: cx(
      'w-full text-left pl-3 pr-2 py-1.5 text-white/90',
      'transition-colors whitespace-nowrap',
      'hover:bg-white/10'
    ),
    // 紧凑版下拉选项（小字体）
    optionCompact: cx(
      'w-full flex items-center justify-start text-left pl-3 pr-2 py-1 text-[11px] text-white/90',
      'transition-colors whitespace-nowrap',
      'hover:bg-white/[0.06]'
    ),
    // 选中态选项
    optionSelected: 'bg-white/15 text-white',
    // 紧凑版选中态
    optionCompactSelected: 'bg-white/[0.08]'
  }
};

export function buttonClass({
  variant = 'outline',
  size = 'md',
  className = ''
} = {}) {
  return cx(theme.buttons.base, theme.buttons.size[size] || theme.buttons.size.md, theme.buttons.variant[variant], className);
}

